import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import {
  trackPageView,
  useInitCookieHub,
} from "@/organization/tracking/cookie-consent/utils/trackingUtils"
import FacebookPixelScript from "@/organization/tracking/scripts/FacebookPixelScript"
import GoogleAnalyticsScript from "@/organization/tracking/scripts/GoogleAnalyticsScript"
import GoogleTagManagerScript from "@/organization/tracking/scripts/GoogleTagManagerScript"
import useHasEntitlement from "@utils/hook/useHasEntitlement"
import usePageLoad from "@utils/hook/usePageLoad"
import { Location } from "history"
import React, { useContext } from "react"

const CookieConsentContext = React.createContext({ hasConsented: false })

export function useCookieConsent() {
  return useContext(CookieConsentContext)
}

const CookieConsentProvider: React.FC = ({ children }) => {
  const activeOrganization = useActiveOrganization()
  usePageLoad((pageLocation: Location) => {
    trackPageView(pageLocation)
  })

  const hasCookieHubConsent = useInitCookieHub({ cookieHubId: COOKIE_HUB_ID })

  // disableCookieBanner reflects that we have disabled our cookie banner per request from the active organization
  // Therefore, if set we cannot assume user consent to initialize our tools for analytics, diagnostics, or session monitoring (e.g., Sentry)
  const disableCookieBanner = useHasEntitlement("disable_cookie_consent")

  const hasConsented = hasCookieHubConsent && !disableCookieBanner

  return (
    <CookieConsentContext.Provider value={{ hasConsented }}>
      {/** Load the Google Analytics */}
      {activeOrganization?.googleAnalyticsId && (
        <GoogleAnalyticsScript googleAnalyticsId={activeOrganization.googleAnalyticsId} />
      )}

      {/** Load the Facebook Pixel */}
      {activeOrganization?.facebookPixelId && (
        <FacebookPixelScript facebookPixelId={activeOrganization.facebookPixelId} />
      )}

      {/** Load the Google Tag Manager */}
      {activeOrganization?.googleTagManagerId && (
        <GoogleTagManagerScript
          googleTagManagerid={activeOrganization.googleTagManagerId}
        />
      )}
      {children}
    </CookieConsentContext.Provider>
  )
}

export default CookieConsentProvider
